import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import { flattenPlanItems } from "../../utils/missionGenerators";

const DEFAULT_CENTER = [12.9716, 77.5946];
const DEFAULT_ZOOM = 16;

// Same base layers as the project's WaypointMap (reused stack).
// Satellite is the default view for construction surveying.
const MAX_ZOOM = 24;
const streetLayer = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  maxZoom: MAX_ZOOM,
  maxNativeZoom: 19,
});
const terrainLayer = L.tileLayer("https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png", {
  attribution: '&copy; <a href="https://opentopomap.org">OpenTopoMap</a> contributors',
  maxZoom: MAX_ZOOM,
  maxNativeZoom: 17,
});
const satelliteLayer = L.tileLayer(
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
  {
    attribution:
      "&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community",
    maxZoom: MAX_ZOOM,
    maxNativeZoom: 19,
  }
);

const BASE_LAYERS = [
  { key: "satellite", label: "Satellite", layer: satelliteLayer },
  { key: "streets", label: "Streets", layer: streetLayer },
  { key: "terrain", label: "Terrain", layer: terrainLayer },
];

export const KIND_COLORS = {
  waypoint: "#2ecc71",
  survey_grid: "#0082f8",
  corridor_scan: "#ff9800",
  structure_scan: "#9c27b0",
  fence_inclusion: "#22c55e",
  fence_exclusion: "#ef4444",
};
const TOOL_TO_DRAW = { survey: "Polygon", structure: "Polygon", corridor: "Line" };
const FENCE_STYLES = {
  inclusion: { color: "#22c55e", fillColor: "#22c55e", fillOpacity: 0.08, weight: 2 },
  exclusion: { color: "#ef4444", fillColor: "#ef4444", fillOpacity: 0.12, weight: 2 },
};
const FENCE_KINDS = ["fence_inclusion", "fence_exclusion"];

function shapeStyle(item) {
  if (item.kind === "fence_inclusion") return FENCE_STYLES.inclusion;
  if (item.kind === "fence_exclusion") return FENCE_STYLES.exclusion;
  return {
    survey_grid: { color: KIND_COLORS.survey_grid, weight: 2, fillColor: KIND_COLORS.survey_grid, fillOpacity: 0.06 },
    corridor_scan: { color: KIND_COLORS.corridor_scan, weight: 3 },
    structure_scan: { color: KIND_COLORS.structure_scan, weight: 2, fillColor: KIND_COLORS.structure_scan, fillOpacity: 0.06 },
  }[item.kind];
}

function pinIcon(number, color) {
  return L.divIcon({
    className: "waypoint-pin-icon",
    html: `<span class="waypoint-pin" style="background:${color}"><span>${number}</span></span>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
}

function homeIcon() {
  return L.divIcon({
    className: "waypoint-pin-icon",
    html: `<span class="home-pin"><span>H</span></span>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });
}

function layerCoords(item) {
  const src = item.polygon || item.polyline || null;
  return src ? src.map(([la, ln]) => L.latLng(la, ln)) : null;
}

function layerRefPoint(layer) {
  if (typeof layer.getLatLng === "function") return layer.getLatLng(); // marker
  const ll = layer.getLatLngs();
  return Array.isArray(ll[0]) ? ll[0][0] : ll[0]; // polygon ring vs polyline
}

function SurveyPlanView({
  items,
  activeTool,
  fitBoundsKey,
  setup,
  selectedId,
  onSelect,
  moveWholePlan,
  onAddWaypoint,
  onCreateShape,
  onUpdateItemGeometry,
  onSetHome,
  onMovePlanStart,
  onTranslatePlanBy,
  onGestureStart,
  onGestureEnd,
  escKey,
}) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const itemLayersRef = useRef(new Map()); // itemId -> leaflet layer
  const previewRef = useRef(null);
  const baseLayerRef = useRef(null);
  const cbRef = useRef({});
  cbRef.current = {
    onAddWaypoint,
    onCreateShape,
    onUpdateItemGeometry,
    onSetHome,
    onSelect,
    moveWholePlan,
    onMovePlanStart,
    onTranslatePlanBy,
    onGestureStart,
    onGestureEnd,
  };
  const toolRef = useRef(activeTool);
  toolRef.current = activeTool;
  const [baseKey, setBaseKey] = useState("satellite");

  /* ── mount ─────────────────────────────────────────────────── */
  useEffect(() => {
    const map = L.map(mapEl.current, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      maxZoom: MAX_ZOOM,
      zoomControl: true,
    });
    satelliteLayer.addTo(map); // satellite is the default view
    baseLayerRef.current = satelliteLayer;
    previewRef.current = L.layerGroup().addTo(map);

    map.on("click", (e) => {
      if (toolRef.current === "waypoint") {
        cbRef.current.onAddWaypoint?.({ lat: e.latlng.lat, lng: e.latlng.lng });
      } else if (toolRef.current === "home") {
        cbRef.current.onSetHome?.({ lat: e.latlng.lat, lng: e.latlng.lng });
      }
    });

    map.on("pm:create", (e) => {
      const tool = toolRef.current;
      const layer = e.layer;
      if (tool === "survey" || tool === "corridor" || tool === "structure") {
        cbRef.current.onCreateShape?.(tool, geoToPayload(tool, layer));
      } else if (tool === "fence_inclusion" || tool === "fence_exclusion") {
        cbRef.current.onCreateShape?.(tool === "fence_inclusion" ? "__fence_in" : "__fence_out",
          geoToPayload("survey", layer));
      }
      map.removeLayer(layer); // React state owns rendering; drop geoman's temp copy
    });

    map.pm.setGlobalOptions({ allowSelfIntersection: false, continueDrawing: false });
    mapRef.current = map;
    return () => map.remove();
  }, []);

  /* ── base-layer switching (themed control on the map) ──────── */
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const entry = BASE_LAYERS.find((b) => b.key === baseKey);
    if (!entry || entry.layer === baseLayerRef.current) return;
    if (baseLayerRef.current && map.hasLayer(baseLayerRef.current)) {
      map.removeLayer(baseLayerRef.current);
    }
    entry.layer.addTo(map);
    baseLayerRef.current = entry.layer;
  }, [baseKey]);

  /* ── draw-mode control ─────────────────────────────────────── */
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.pm.disableDraw();
    if (TOOL_TO_DRAW[activeTool]) {
      const color =
        activeTool === "survey" ? KIND_COLORS.survey_grid
        : activeTool === "corridor" ? KIND_COLORS.corridor_scan
        : KIND_COLORS.structure_scan;
      map.pm.enableDraw(TOOL_TO_DRAW[activeTool], {
        templineStyle: { color },
        hintlineStyle: { color, dashArray: [5, 5] },
      });
    } else if (activeTool === "fence_inclusion" || activeTool === "fence_exclusion") {
      const key = activeTool === "fence_inclusion" ? "inclusion" : "exclusion";
      map.pm.enableDraw("Polygon", {
        templineStyle: FENCE_STYLES[key],
        hintlineStyle: { ...FENCE_STYLES[key], dashArray: [5, 5] },
      });
    }
  }, [activeTool]);

  /* ── sync mission-item layers (create/update/remove/drag) ──── */
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    items.forEach((item) => {
      let layer = itemLayersRef.current.get(item.id);
      const want = layerCoords(item);

      if (item.kind === "waypoint") {
        const pos = L.latLng(item.latitude, item.longitude);
        if (layer) {
          if (!layer.getLatLng().equals(pos)) layer.setLatLng(pos);
          layer.setIcon(pinIcon(item.order ?? 1, KIND_COLORS.waypoint));
          return;
        }
        const mk = L.marker(pos, {
          icon: pinIcon(item.order ?? 1, KIND_COLORS.waypoint),
          draggable: true,
        }).addTo(map);
        mk.on("dragstart", () => {
          cbRef.current.onGestureStart?.();
          if (!cbRef.current.moveWholePlan) return;
          mk._planStart = mk.getLatLng();
          cbRef.current.onMovePlanStart?.();
        });
        mk.on("drag", () => {
          if (mk._planStart) {
            const p = mk.getLatLng();
            cbRef.current.onTranslatePlanBy?.({
              dLat: p.lat - mk._planStart.lat,
              dLng: p.lng - mk._planStart.lng,
            });
            return;
          }
        });
        mk.on("dragend", () => {
          const isPlanMove = !!mk._planStart;
          mk._planStart = null;
          if (!isPlanMove) {
            cbRef.current.onUpdateItemGeometry?.(item.id, {
              latitude: mk.getLatLng().lat,
              longitude: mk.getLatLng().lng,
            });
          }
          cbRef.current.onGestureEnd?.();
        });
        mk.on("click", () => cbRef.current.onSelect?.(item.id));
        itemLayersRef.current.set(item.id, mk);
        return;
      }

      if (!want) return;

      if (layer) {
        if (item.polygon && layer.setLatLngs) layer.setLatLngs([want]);
        else if (item.polyline) layer.setLatLngs(want);
        return;
      }
      let newLayer;
      if (item.polygon) {
        newLayer = L.polygon(want, shapeStyle(item)).addTo(map);
        if (FENCE_KINDS.includes(item.kind)) {
          newLayer.bindTooltip(
            item.kind === "fence_inclusion" ? "Inclusion geofence" : "Exclusion geofence",
            { sticky: true }
          );
        }
      } else {
        newLayer = L.polyline(want, shapeStyle(item)).addTo(map);
      }
      // Vertex-editable AND movable as a whole shape.
      newLayer.pm.enable({ allowSelfIntersection: false, draggable: true });
      const currentCoords = () =>
        item.polygon
          ? newLayer.getLatLngs()[0].map((p) => [p.lat, p.lng])
          : newLayer.getLatLngs().map((p) => [p.lat, p.lng]);
      newLayer.on("pm:dragstart", () => {
        cbRef.current.onGestureStart?.();
        if (!cbRef.current.moveWholePlan) return;
        newLayer._planStart = layerRefPoint(newLayer);
        cbRef.current.onMovePlanStart?.();
      });
      newLayer.on("pm:drag", () => {
        if (cbRef.current.moveWholePlan && newLayer._planStart) {
          const p = layerRefPoint(newLayer);
          cbRef.current.onTranslatePlanBy?.({
            dLat: p.lat - newLayer._planStart.lat,
            dLng: p.lng - newLayer._planStart.lng,
          });
          return;
        }
        cbRef.current.onUpdateItemGeometry?.(item.id, currentCoords());
      });
      newLayer.on("pm:dragend", () => {
        const isPlanMove = !!newLayer._planStart;
        newLayer._planStart = null;
        if (!isPlanMove) {
          cbRef.current.onUpdateItemGeometry?.(item.id, currentCoords());
        }
        cbRef.current.onGestureEnd?.();
      });
      newLayer.on("pm:edit", () => {
        cbRef.current.onGestureEnd?.();
        cbRef.current.onUpdateItemGeometry?.(item.id, currentCoords());
      });
      newLayer.on("click", () => cbRef.current.onSelect?.(item.id));
      itemLayersRef.current.set(item.id, newLayer);
    });

    // remove layers for deleted items
    itemLayersRef.current.forEach((layer, id) => {
      if (!items.find((it) => it.id === id)) {
        map.removeLayer(layer);
        itemLayersRef.current.delete(id);
      }
    });
  }, [items]);

  /* ── highlight the selected item's layer ───────────────────── */
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    items.forEach((item) => {
      const layer = itemLayersRef.current.get(item.id);
      if (!layer || item.kind === "waypoint" || !layer.setStyle) return;
      const base = shapeStyle(item);
      if (selectedId === item.id) {
        layer.setStyle({
          ...base,
          weight: (base.weight || 2) + 2,
          fillOpacity: Math.min(0.28, (base.fillOpacity || 0.06) + 0.12),
          dashArray: "1",
        });
        layer.bringToFront?.();
      } else {
        layer.setStyle(base);
      }
    });
  }, [selectedId, items]);

  /* ── Esc: cancel any active geoman draw ────────────────────── */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !escKey) return;
    map.pm.disableDraw();
    map.pm.disableGlobalEditMode?.();
  }, [escKey]);

  /* ── whole-plan move mode: grab cursor over the map ─────────── */
  useEffect(() => {
    const container = mapRef.current?.getContainer();
    if (container) container.classList.toggle("cs-plan-move", !!moveWholePlan);
  }, [moveWholePlan]);

  /* ── home marker: draggable launch point ───────────────────── */
  const homeMarkerRef = useRef(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!homeMarkerRef.current) {
      homeMarkerRef.current = L.marker(DEFAULT_CENTER, {
        icon: homeIcon(),
        draggable: true,
      });
      homeMarkerRef.current.on("dragend", () => {
        const p = homeMarkerRef.current.getLatLng();
        cbRef.current.onSetHome?.({ lat: p.lat, lng: p.lng });
      });
    }
    const mk = homeMarkerRef.current;
    const pos = setup?.homeLocation
      ? L.latLng(setup.homeLocation.lat, setup.homeLocation.lng)
      : null;
    if (pos && !mk.getLatLng().equals(pos)) mk.setLatLng(pos);
    if (pos) mk.addTo(map);
    else map.removeLayer(mk);
  }, [setup]);

  /* ── generated-grid previews + numbered pins ───────────────── */
  useEffect(() => {
    const group = previewRef.current;
    if (!group) return;
    group.clearLayers();

    const home = setup?.homeLocation
      ? L.latLng(setup.homeLocation.lat, setup.homeLocation.lng)
      : null;

    // global running index so pins number continuously across items
    // (fence polygons are boundaries, not waypoints — excluded)
    let seq = 0;
    let firstWp = null;
    let lastWp = null;
    items
      .filter((item) => !FENCE_KINDS.includes(item.kind))
      .forEach((item) => {
        const wps = flattenPlanItems([item]);
        const color = KIND_COLORS[item.kind] || "#0082f8";
        if (wps.length > 1) {
          L.polyline(wps.map((w) => [w.latitude, w.longitude]), { color, weight: 2.5, opacity: 0.9 }).addTo(group);
        }
        wps.forEach((w) => {
          seq += 1;
          if (!firstWp) firstWp = [w.latitude, w.longitude];
          lastWp = [w.latitude, w.longitude];
          L.marker([w.latitude, w.longitude], { icon: pinIcon(seq, color), interactive: false }).addTo(group);
        });
      });

    // Takeoff climb line: home -> first waypoint
    if (home && firstWp) {
      L.polyline([home, firstWp], {
        color: "#4cceac", weight: 2, dashArray: "4 6", opacity: 0.9,
      }).addTo(group);
    }
    // RTL return line: last waypoint -> home
    if (home && lastWp && setup?.rtlAtEnd && lastWp !== firstWp) {
      L.polyline([lastWp, home], {
        color: "#00af64", weight: 2.5, dashArray: "8 8", opacity: 0.95,
      }).addTo(group);
    }

    // route line connecting consecutive items' first/last waypoints
    const anchors = [];
    items
      .filter((item) => !FENCE_KINDS.includes(item.kind))
      .forEach((item) => {
        const wps = flattenPlanItems([item]);
        if (wps.length) anchors.push(L.latLng(wps[0].latitude, wps[0].longitude));
      });
    if (anchors.length > 1) {
      L.polyline(anchors, { color: "#64748b", weight: 1.5, dashArray: "6 8", opacity: 0.7 }).addTo(group);
    }
  }, [items, setup]);

  /* ── fit bounds when requested ─────────────────────────────── */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !fitBoundsKey) return;
    const pts = [];
    items.forEach((it) => {
      (it.polygon || it.polyline || []).forEach(([la, ln]) => pts.push(L.latLng(la, ln)));
      if (it.kind === "waypoint") pts.push(L.latLng(it.latitude, it.longitude));
    });
    if (setup?.homeLocation) {
      pts.push(L.latLng(setup.homeLocation.lat, setup.homeLocation.lng));
    }
    if (pts.length) map.fitBounds(L.latLngBounds(pts).pad(0.15));
  }, [fitBoundsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="cs-map-wrap">
      <div ref={mapEl} className="cs-map" />
      <div className="cs-layer-switch" role="tablist" aria-label="Map base layer">
        {BASE_LAYERS.map((b) => (
          <button
            key={b.key}
            type="button"
            className={`cs-layer-btn ${baseKey === b.key ? "active" : ""}`}
            onClick={() => setBaseKey(b.key)}
          >
            {b.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function geoToPayload(tool, layer) {
  if (tool === "corridor") {
    return layer.getLatLngs().map((p) => [p.lat, p.lng]);
  }
  const ring = layer.getLatLngs()[0] || [];
  const coords = ring.map((p) => [p.lat, p.lng]);
  coords.push(coords[0]);
  return coords;
}

export default SurveyPlanView;
